import ArticleLayout from './ArticleLayout'
import type { ProfessionPageConfig } from './professions/types'
import { acompanhamentoPath, bookingLabel, conversaPath, individualBookingUrl, valoresPath, whatsAppUrlFor, youtubeChannelUrl } from './siteDestinations'
import { ArticleCard, ArticleCards, ArticleClosing, ArticleExample, ArticleFaq, ArticleSection, ArticleSteps } from './ui/Article'

/** Possibilidades por profissão, com o convite para a primeira conversa. */
export default function ProfessionPage({ config }: { config: ProfessionPageConfig }) {
  const steps = [
    { title: 'Conte.', body: 'Descreva a tarefa em português, do jeito que falaria para um colega: o que você tem, o que quer no fim.' },
    { title: 'Faça.', body: config.steps.do },
    { title: 'Confira.', body: config.steps.check },
    { title: 'Guarde.', body: 'Salve o seu pedido num arquivo de texto, dentro da mesma pasta.' },
    { title: 'Retome.', body: config.steps.resume },
  ]

  return (
    <ArticleLayout
      eyebrow="Por profissão"
      title={config.heading}
      intro={config.intro}
      rogerio={config.rogerio}
      facts={[[ '10+', 'anos ensinando adultos' ], [ '3 tarefas', 'da sua área para explorar' ], [ '30 min', 'para conversar sobre o seu caso' ], [ 'Individual', 'na sua tela, no seu ritmo' ]]}
      flow={[...config.tasks.map(task => task.title.toLocaleLowerCase('pt-BR')), 'escolher uma tarefa', 'fazer junto', 'conferir', 'seguir sozinho']}
      secondary={{ href: '#' + config.key + '-tarefas', label: 'Explorar as tarefas' }}
    >
      <ArticleSection id={`${config.key}-tarefas`} eyebrow="Possibilidades" title="Três tarefas para começar" hint="Exemplos do que dá para explorar. Cada caso pede o seu tamanho.">
        <ArticleCards>
          {config.tasks.map((task) => (
            <ArticleCard key={task.title} tone={task.tone} title={task.title}>
              <ArticleExample brings={task.brings} helps={task.helps} continues={task.continues} />
            </ArticleCard>
          ))}
        </ArticleCards>
      </ArticleSection>

      <ArticleSection id={`${config.key}-como`} eyebrow="Um fio que você pode retomar" title="Como isso acontece">
        <ArticleSteps steps={steps} />
        <p>
          A ferramenta é escolhida conforme a tarefa, os arquivos e o que você já usa. Na <a href={conversaPath}>primeira conversa</a>,
          você me conta o que faz no computador. Eu mostro onde a IA entra no seu caso e a gente escolhe um primeiro passo.
          Se continuar, <a href={acompanhamentoPath}>a gente faz junto até você seguir sozinho</a>.
        </p>
      </ArticleSection>

      <ArticleSection id={`${config.key}-duvidas`} eyebrow="Ainda tem alguma ponta solta?" title="Dúvidas">
        <ArticleFaq idPrefix={`${config.key}-faq`} items={config.faq} />
      </ArticleSection>

      <ArticleClosing
        eyebrow="Se quiser companhia"
        title={config.closing.title}
        action={{ href: individualBookingUrl, label: bookingLabel, external: true }}
        secondary={[
          { href: valoresPath, label: 'Ver os valores' },
          { href: youtubeChannelUrl, label: 'Ver o canal no YouTube', external: true },
        ]}
        note={<>Você escolhe o horário na minha agenda. Formato, duração e investimento do acompanhamento a gente combina na conversa. <a href={whatsAppUrlFor(config.whatsappMessage)} target="_blank" rel="noopener noreferrer">Prefere tirar uma dúvida antes? Me chame no WhatsApp ↗</a></>}
        rogerio={{ pose: 'concluido', text: config.closing.rogerio }}
      >
        {config.closing.text} Você me conta o que faz no computador. Em 30 minutos, eu mostro onde a IA entra no seu caso e você sai com um primeiro passo para tentar sozinho. A primeira conversa é gratuita.
      </ArticleClosing>
    </ArticleLayout>
  )
}
